import * as React from 'react';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import RangeDatePicker from '../../components/Layout/DatePicker.tsx';
import 'react-datepicker/dist/react-datepicker.css';
import '../../components/Layout/DatePicker.css';
import { CopyIcon, HashIcon, CheckIcon } from '../../components/icons/Icons.tsx';
import { Send, Download, Filter, RefreshCw, CheckCircle, ArrowRight, AlertTriangle, Clock, DollarSign, Users, FileText, Shield, X, Upload, Calendar, ArrowLeft, TrendingUp, TrendingDown, Target, Briefcase, Globe, Activity, CreditCard, Zap, Percent } from 'react-feather';

// --- Reused from TransactionsPage ---

// Mock Data Structure (Same as TransactionsPage, but filtered for Acquiring)
interface Transaction {
  id: string;
  transactionDate: string;
  partner: string;
  product: 'Acquiring'; // Specific to this page
  type: string;
  amount: number;
  currency: string;
  status: 'Completed' | 'Pending' | 'Failed';
  location?: string;
  logoUrl?: string | null;
  category?: 'Credit' | 'Debit' | 'Correction';
  reference?: string;
  description?: string;
  reason?: string;
  approvedBy?: string;
  correctionDetails?: {
    correctionType: string;
    reason: string;
    documentUrl?: string;
    documentName?: string;
  };
  // Acquiring-specific fields
  cardType?: string;
  feeAmount?: number;
  merchantId?: string;
  terminalId?: string;
}

// Partner data interface (Reused)
interface Partner {
  name: string;
  location: string;
  logoUrl?: string | null;
}

// DropdownFilterProps interface (Reused)
interface DropdownFilterProps {
    label: string;
    options: string[];
    selectedValue: string;
    onChange: (value: string) => void;
}

// DropdownFilter Component (Reused)
const DropdownFilter: React.FC<DropdownFilterProps> = ({ label, options, selectedValue, onChange }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [hoveredOption, setHoveredOption] = React.useState<string | null>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const handleSelect = (option: string) => {
        onChange(option);
        setIsOpen(false);
    };

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button
                style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer',
                    minWidth: '140px',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#adb5bd'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
            >
                {label}: <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedValue}</span>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? "m18 14-6-6-6 6" : "m6 10 6 6 6-6"} />
                </svg>
            </button>
            {isOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '160px', padding: '4px 0' }}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '200px', overflowY: 'auto' }}>
                        {options.map(option => (
                            <li
                                key={option}
                                style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#374151',
                                    backgroundColor: hoveredOption === option ? '#f9fafb' : 'white'
                                }}
                                onClick={() => handleSelect(option)}
                                onMouseEnter={() => setHoveredOption(option)}
                                onMouseLeave={() => setHoveredOption(null)}
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// Partner Location Mapping (Reused)
const partnerLocations: { [key: string]: string } = {
  'Amazon': 'UAE', 'Shopify': 'KSA', 'Noon': 'UAE', 'Namshi': 'KSA',
  'Carrefour': 'UAE', 'IKEA': 'KSA', 'Adidas': 'UAE', 'H&M': 'Qatar',
  'Xcite': 'Kuwait', 'Jarir Bookstore': 'Kuwait', 'Sharaf DG': 'Bahrain',
  'LuLu Hypermarket': 'Bahrain', 'Talabat': 'Qatar',
  'Extra Electronics Superstore': 'KSA', 'Danube Hypermarket Online': 'UAE',
};

// Partner Logo Mapping (Reused)
const partnerLogos: { [key: string]: string | null } = {
  'Amazon': 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
  'Shopify': 'https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg',
  'Noon': 'https://upload.wikimedia.org/wikipedia/commons/c/ca/Noon_Website_Logo.svg',
  'Namshi': null, 'Carrefour': 'https://upload.wikimedia.org/wikipedia/commons/5/5b/Carrefour_logo.svg',
  'IKEA': 'https://upload.wikimedia.org/wikipedia/commons/c/c5/Ikea_logo.svg',
  'Adidas': 'https://upload.wikimedia.org/wikipedia/commons/2/20/Adidas_Logo.svg',
  'H&M': 'https://upload.wikimedia.org/wikipedia/commons/5/53/H%26M-Logo.svg',
  'Xcite': 'https://media.xcite.com/media/logo/stores/1/New-Xcite-Logo-EnAr.png',
  'Jarir Bookstore': 'https://upload.wikimedia.org/wikipedia/en/5/58/Jarir_Bookstore_logo.svg',
  'Sharaf DG': 'https://uae.sharafdg.com/wp-content/uploads/2020/11/logo-sharafdg.png',
  'LuLu Hypermarket': null, 'Talabat': 'https://upload.wikimedia.org/wikipedia/commons/b/b3/Talabat_logo.svg',
  'Extra Electronics Superstore': null, 'Danube Hypermarket Online': null,
};

// Function to generate random ISO date string (Reused)
const getRandomISODate = (baseDate: string, index: number): string => {
  const date = new Date(baseDate);
  const hour = Math.floor(Math.random() * 24);
  const minute = Math.floor(Math.random() * 60);
  const second = Math.floor(Math.random() * 60);
  date.setUTCHours(hour, minute, second, 0);
  return date.toISOString();
};

// Function to generate a simple UUID v4 (Reused)
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Helper function to determine transaction category (Reused - simplified input type)
const getTransactionCategoryBase = (transaction: Pick<Transaction, 'type' | 'amount'>): 'Credit' | 'Debit' | 'Correction' => {
    if (transaction.type.toLowerCase().includes('correction')) return 'Correction';
    if (transaction.amount >= 0) return 'Credit';
    return 'Debit';
};

// Card types for Acquiring transactions
const cardTypes = ['Visa', 'MasterCard', 'American Express', 'Discover', 'JCB', 'Diners Club', 'UnionPay'];

// Base Mock Transactions for Acquiring
const rawMockTransactionsBase: Omit<Transaction, 'id' | 'location' | 'logoUrl' | 'product'>[] = [
  { transactionDate: '2025-04-21', partner: 'Amazon', type: 'Card Payment', amount: 3500.00, currency: 'AED', status: 'Completed', cardType: 'Visa', feeAmount: 87.50 },
  { transactionDate: '2025-04-22', partner: 'Amazon', type: 'Card Refund', amount: -1200.00, currency: 'AED', status: 'Completed', cardType: 'Visa', feeAmount: -30.00 },
  { transactionDate: '2025-04-23', partner: 'Amazon', type: 'Processing Fee', amount: -175.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Amazon', type: 'Revenue corrections', amount: 150.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Carrefour', type: 'Card Payment', amount: 2800.00, currency: 'AED', status: 'Completed', cardType: 'MasterCard', feeAmount: 70.00 },
  { transactionDate: '2025-04-22', partner: 'Carrefour', type: 'Settlement', amount: 2730.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'H&M', type: 'Card Payment', amount: 1500.00, currency: 'QAR', status: 'Completed', cardType: 'American Express', feeAmount: 45.00 },
  { transactionDate: '2025-04-24', partner: 'H&M', type: 'Card Refund', amount: -500.00, currency: 'QAR', status: 'Pending', cardType: 'American Express', feeAmount: -15.00 },
  { transactionDate: '2025-04-21', partner: 'Xcite', type: 'Card Payment', amount: 1200.00, currency: 'KWD', status: 'Completed', cardType: 'Visa', feeAmount: 36.00 },
  { transactionDate: '2025-04-22', partner: 'Xcite', type: 'Revenue corrections', amount: 80.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Xcite', type: 'Non-revenue corrections', amount: -20.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Xcite', type: 'Card Refund', amount: -300.00, currency: 'KWD', status: 'Completed', cardType: 'Visa', feeAmount: -9.00 },
  { transactionDate: '2025-04-21', partner: 'LuLu Hypermarket', type: 'Card Payment', amount: 3500.00, currency: 'BHD', status: 'Completed', cardType: 'MasterCard', feeAmount: 105.00 },
  { transactionDate: '2025-04-22', partner: 'LuLu Hypermarket', type: 'Withdrawal', amount: -1500.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'LuLu Hypermarket', type: 'Fixed Fee', amount: -75.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'LuLu Hypermarket', type: 'Service Fee', amount: -125.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-27', partner: 'Danube Hypermarket Online', type: 'Card Payment', amount: 950.00, currency: 'AED', status: 'Pending', cardType: 'Discover', feeAmount: 28.50 },
  { transactionDate: '2025-04-28', partner: 'Danube Hypermarket Online', type: 'Processing Fee', amount: -47.50, currency: 'AED', status: 'Completed' },
];

// Process raw transactions specifically for Acquiring
const processedAcquiringTransactions: Transaction[] = rawMockTransactionsBase
  .map((tx, index) => ({
    ...tx,
    id: generateUUID(),
    product: 'Acquiring', // Assign Acquiring product
    transactionDate: getRandomISODate(tx.transactionDate, index),
    location: partnerLocations[tx.partner] || 'N/A',
    logoUrl: partnerLogos[tx.partner],
    category: getTransactionCategoryBase(tx as any), // Derive category
    merchantId: `MID${Math.floor(10000 + Math.random() * 90000)}`,
    terminalId: tx.type.includes('Card') ? `TID${Math.floor(1000 + Math.random() * 9000)}` : undefined,
  } as Transaction))
  .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());

// Helper function for currency formatting (Reused)
const formatCurrency = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

// Helper to get partner initials (Reused)
const getInitials = (name: string): string => {
  if (!name) return '??';
  const words = name.split(' ').filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];
  return (firstInitial + lastInitial).toUpperCase();
};

// Helper function to format date and time (Reused)
const formatDateTime = (isoString: string): { date: string, time: string } => {
  try {
    const dateObj = new Date(isoString);
    const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    const formattedTime = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    console.error("Error formatting date:", isoString, error);
    return { date: 'Invalid Date', time: '' };
  }
};

// Status Badge Component (Reused)
const StatusBadge: React.FC<{ status: Transaction['status'] }> = ({ status }) => {
  let styles: React.CSSProperties = {
    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500,
    textTransform: 'capitalize', display: 'inline-block',
  };
  switch (status) {
    case 'Completed': styles = { ...styles, backgroundColor: '#ecfdf5', color: '#059669' }; break;
    case 'Pending': styles = { ...styles, backgroundColor: '#fefce8', color: '#d97706' }; break;
    case 'Failed': styles = { ...styles, backgroundColor: '#fef2f2', color: '#dc2626' }; break;
    default: styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' }; break;
  }
  return <span style={styles}>{status}</span>;
};

// Category Badge Component (Reused)
const CategoryBadge: React.FC<{ category: 'Credit' | 'Debit' | 'Correction' }> = ({ category }) => {
    let styles: React.CSSProperties = {
      borderRadius: '12px', fontSize: '12px', fontWeight: 500,
      textTransform: 'capitalize', display: 'inline-block',
    };
    switch (category) {
      case 'Credit': styles = { ...styles, color: '#059669' }; break;
      case 'Debit': styles = { ...styles, color: '#ef4444' }; break;
      case 'Correction': styles = { ...styles, color: '#4f46e5' }; break;
      default: styles = { ...styles, color: '#64748b' }; break;
    }
    return <span style={styles}>{category}</span>;
};

// Transaction Details Panel Component (Adapted for Acquiring)
interface TransactionDetailsPanelProps {
  transaction: Transaction | null;
  onClose: () => void;
  isVisible: boolean;
}

const TransactionDetailsPanel: React.FC<TransactionDetailsPanelProps> = ({ transaction, onClose, isVisible }) => {
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((event.target as Element).closest('[data-transaction-row="true"]')) return; // Skip if clicking a table row
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) onClose();
    };
    if (isVisible) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, onClose]);

  if (!transaction) return null;

  const dateTime = formatDateTime(transaction.transactionDate);
  const category = getTransactionCategoryBase(transaction);

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed', top: '64px', right: 0, width: '380px', height: 'calc(100vh - 64px)',
        backgroundColor: 'white', boxShadow: '-4px 0 10px rgba(0, 0, 0, 0.1)', zIndex: 1000,
        overflowY: 'auto', transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease-out', padding: '24px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#111827' }}>Transaction Details</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          <StatusBadge status={transaction.status} />
          {category === 'Correction' && <span style={{ backgroundColor: '#eef2ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '16px', fontSize: '14px', fontWeight: 500 }}>Correction</span>}
        </div>

        <div style={{ marginBottom: '32px' }}>
          <p style={{ color: '#6b7280', fontSize: '16px', margin: '0 0 8px 0', fontWeight: 500 }}>Amount</p>
          <h3 style={{ fontSize: '32px', fontWeight: 600, margin: 0, color: transaction.amount >= 0 ? '#047857' : '#dc2626' }}>
            {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount, transaction.currency)}
          </h3>
          {transaction.feeAmount && (
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '8px 0 0 0' }}>
              Fee: {formatCurrency(transaction.feeAmount, transaction.currency)}
            </p>
          )}
        </div>

        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '24px 0' }}></div>

        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Partner</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.partner}</div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Date & Time</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{dateTime.date}, {dateTime.time}</div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Type</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.type}</div>
        </div>
        {transaction.cardType && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Card Type</p>
            <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.cardType}</div>
          </div>
        )}
        {transaction.merchantId && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Merchant ID</p>
            <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.merchantId}</div>
          </div>
        )}
        {transaction.terminalId && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Terminal ID</p>
            <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.terminalId}</div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '40px', color: '#9ca3af', fontSize: '14px' }}>
            <Clock size={16} style={{ marginRight: '6px' }} />
            Transaction ID: {transaction.id.substring(0, 8)}
        </div>
      </div>
    </div>
  );
};

// Widget Component for Country Balance
interface CountryBalanceWidgetProps {
  country: string;
  balance: number;
  currency: string;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

const CountryBalanceWidget: React.FC<CountryBalanceWidgetProps> = ({ country, balance, currency, change, trend }) => {
  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '20px', 
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          backgroundColor: '#f0fdf4', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginRight: '12px'
        }}>
          <Globe size={18} color="#10b981" />
        </div>
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: 0 }}>{country}</h3>
      </div>
      
      <div style={{ marginBottom: '8px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0', color: '#111827' }}>
          {formatCurrency(balance, currency)}
        </h2>
      </div>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        fontSize: '14px', 
        fontWeight: 500,
        color: trend === 'up' ? '#10b981' : trend === 'down' ? '#ef4444' : '#6b7280'
      }}>
        {trend === 'up' ? <TrendingUp size={16} style={{ marginRight: '4px' }} /> : 
         trend === 'down' ? <TrendingDown size={16} style={{ marginRight: '4px' }} /> : null}
        {change > 0 ? '+' : ''}{formatCurrency(change, currency)}
      </div>
    </div>
  );
};

// Metric Widget Component
interface MetricWidgetProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  secondaryValue?: string;
  secondaryColor?: string;
  bgColor?: string;
  iconColor?: string;
}

const MetricWidget: React.FC<MetricWidgetProps> = ({ 
  title, 
  value, 
  icon,
  secondaryValue, 
  secondaryColor = '#10b981',
  bgColor = '#f0fdf4',
  iconColor = '#10b981'
}) => {
  // Render the icon directly, applying size and color
  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      try {
        return React.cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, { size: 18, color: iconColor });
      } catch (e) {
        console.error("Could not clone icon element:", e);
        return icon;
      }
    } else if (typeof icon === 'function') {
       const IconComponent = icon as React.ElementType;
       return <IconComponent size={18} color={iconColor} />;
    }
    return null;
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '20px', 
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          backgroundColor: bgColor, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginRight: '12px'
        }}>
          {renderIcon()}
        </div>
        <h3 style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', margin: 0 }}>{title}</h3>
      </div>
      
      <div style={{ marginBottom: secondaryValue ? '8px' : 0 }}>
        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0', color: '#111827' }}>
          {value}
        </h2>
      </div>
      
      {secondaryValue && (
        <div style={{ fontSize: '14px', fontWeight: 500, color: secondaryColor }}>
          {secondaryValue}
        </div>
      )}
    </div>
  );
};

// Main Balance Widget Component
interface MainBalanceWidgetProps {
  title: string;
  totalBalance: number;
  change: number;
  currency: string;
  icon?: React.ReactNode;
}

const MainBalanceWidget: React.FC<MainBalanceWidgetProps> = ({ 
  title = "Total Balance",
  totalBalance, 
  change, 
  currency, 
  icon = <DollarSign />
}) => {
  const trend = change >= 0 ? 'up' : 'down';
  const changeText = `${change >= 0 ? '+' : ''}${formatCurrency(change, currency)}`;
  const changeColor = trend === 'up' ? '#10b981' : '#ef4444';
  const defaultBgColor = '#f0fdf4';
  const defaultIconColor = '#10b981';

  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      try {
        return React.cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, { size: 18, color: defaultIconColor });
      } catch (e) {
        console.error("Could not clone icon element:", e);
        return icon; 
      }
    } else if (typeof icon === 'function') {
       const IconComponent = icon as React.ElementType;
       return <IconComponent size={18} color={defaultIconColor} />;
    }
    return null; 
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          backgroundColor: defaultBgColor, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginRight: '12px'
        }}>
          {renderIcon()}
        </div>
        <h3 style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', margin: 0 }}>{title}</h3>
      </div>
      
      <div style={{ marginBottom: '8px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0', color: '#111827' }}>
          {formatCurrency(totalBalance, currency)}
        </h2>
      </div>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        fontSize: '14px', 
        fontWeight: 500,
        color: changeColor
      }}>
        {trend === 'up' ? <TrendingUp size={16} style={{ marginRight: '4px' }} /> : 
         <TrendingDown size={16} style={{ marginRight: '4px' }} />}
        {changeText}
      </div>
    </div>
  );
};

// --- Acquiring Wallet Page Component ---

const AcquiringWalletPage: React.FC = () => {
  const [transactions, setTransactions] = React.useState<Transaction[]>(processedAcquiringTransactions);
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(null);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [hoveredIdIcon, setHoveredIdIcon] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  // Filter States
  const [selectedDateRange, setSelectedDateRange] = React.useState<string>('All Time');
  const [selectedType, setSelectedType] = React.useState<string>('All Types');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('All Countries');
  const [selectedStatus, setSelectedStatus] = React.useState<string>('All Statuses');
  const [selectedCardType, setSelectedCardType] = React.useState<string>('All Cards'); // Added for Acquiring
  const [customStartDate, setCustomStartDate] = React.useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = React.useState<Date | null>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = React.useState<boolean>(false);

  // Selected transaction state for details panel
  const [selectedTransaction, setSelectedTransaction] = React.useState<Transaction | null>(null);
  const [isPanelVisible, setIsPanelVisible] = React.useState<boolean>(false);

  // Filter Options (Derived from Acquiring transactions)
  const typeOptions = React.useMemo(
    () => ['All Types', ...Array.from(new Set(transactions.map(tx => tx.type))).sort()],
    [transactions]
  );
  const countryOptions = React.useMemo(
    () => ['All Countries', ...Array.from(new Set(transactions.map(tx => tx.location || 'N/A'))).sort()],
    [transactions]
  );
  const statusOptions = React.useMemo(() => ['All Statuses', 'Completed', 'Pending', 'Failed'], []);
  const cardTypeOptions = React.useMemo(
    () => ['All Cards', ...Array.from(new Set(transactions.filter(tx => tx.cardType).map(tx => tx.cardType!))).sort()],
    [transactions]
  );
  const dateRangeOptions = ['All Time', 'Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Custom date'];

  // Filtering Logic
  const filteredTransactions = React.useMemo(() => {
      return transactions.filter(tx => {
          const txDate = new Date(tx.transactionDate);

          // Date Range Filter
          let dateMatch = true;
          if (selectedDateRange === 'Custom date') {
              if (customStartDate && customEndDate) {
                  const adjustedEndDate = new Date(customEndDate);
                  adjustedEndDate.setHours(23, 59, 59, 999);
                  dateMatch = txDate >= customStartDate && txDate <= adjustedEndDate;
              } else if (customStartDate) {
                  dateMatch = txDate >= customStartDate;
              } else {
                  dateMatch = true;
              }
          } else if (selectedDateRange === 'Last 7 Days') {
              const startDate = new Date(); startDate.setDate(startDate.getDate() - 7); dateMatch = txDate >= startDate;
          } else if (selectedDateRange === 'Last 30 Days') {
              const startDate = new Date(); startDate.setDate(startDate.getDate() - 30); dateMatch = txDate >= startDate;
          } else if (selectedDateRange === 'Last 90 Days') {
              const startDate = new Date(); startDate.setDate(startDate.getDate() - 90); dateMatch = txDate >= startDate;
          }

          // Type Filter
          const typeMatch = selectedType === 'All Types' || tx.type === selectedType;

          // Country Filter
          const countryMatch = selectedCountry === 'All Countries' || tx.location === selectedCountry;

          // Status Filter
          const statusMatch = selectedStatus === 'All Statuses' || tx.status === selectedStatus;

          // Card Type Filter (Specific to Acquiring)
          const cardTypeMatch = selectedCardType === 'All Cards' || tx.cardType === selectedCardType;

          // Search Term Filter
          const matchesSearch = searchTerm === '' ||
                                tx.partner.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.amount.toString().includes(searchTerm) ||
                                tx.currency.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                (tx.location && tx.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                (tx.cardType && tx.cardType.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                (tx.merchantId && tx.merchantId.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                (tx.terminalId && tx.terminalId.toLowerCase().includes(searchTerm.toLowerCase()));

          return dateMatch && typeMatch && countryMatch && statusMatch && cardTypeMatch && matchesSearch;
      });
  }, [transactions, searchTerm, selectedDateRange, selectedType, selectedCountry, selectedStatus, selectedCardType, customStartDate, customEndDate]);

  // Calculate Acquiring-specific metrics
  const acquiringMetrics = React.useMemo(() => {
      // Process volume
      const totalProcessVolume = filteredTransactions
          .filter(tx => tx.type.includes('Card Payment'))
          .reduce((sum, tx) => sum + tx.amount, 0);
          
      // Fees collected
      const totalFees = filteredTransactions
          .filter(tx => tx.feeAmount !== undefined)
          .reduce((sum, tx) => sum + (tx.feeAmount || 0), 0);
          
      // Refund volume
      const refundVolume = filteredTransactions
          .filter(tx => tx.type.includes('Refund'))
          .reduce((sum, tx) => Math.abs(sum) + Math.abs(tx.amount), 0);
          
      // Active merchants count (using merchantId)
      const activeMerchants = new Set(
          filteredTransactions
              .filter(tx => tx.merchantId && tx.status === 'Completed')
              .map(tx => tx.merchantId)
      ).size;
      
      // Active terminals count
      const activeTerminals = new Set(
          filteredTransactions
              .filter(tx => tx.terminalId && tx.status === 'Completed')
              .map(tx => tx.terminalId)
      ).size;
      
      // Calculate card distribution
      const cardDistribution = filteredTransactions
          .filter(tx => tx.cardType && tx.type.includes('Card Payment'))
          .reduce((acc, tx) => {
              if (!tx.cardType) return acc;
              acc[tx.cardType] = (acc[tx.cardType] || 0) + tx.amount;
              return acc;
          }, {} as Record<string, number>);
          
      return {
          totalProcessVolume,
          totalFees,
          refundVolume,
          activeMerchants,
          activeTerminals,
          cardDistribution
      };
  }, [filteredTransactions]);
  
  // Calculate current balance per country and currency
  const balanceByCountry = React.useMemo(() => {
      const countryBalances: { [key: string]: { [currency: string]: number } } = {};
      const previousCountryBalances: { [key: string]: { [currency: string]: number } } = {};
      
      filteredTransactions.forEach(tx => {
          const country = tx.location || 'Unknown';
          if (!countryBalances[country]) {
              countryBalances[country] = {};
              previousCountryBalances[country] = {};
          }
          
          if (!countryBalances[country][tx.currency]) {
              countryBalances[country][tx.currency] = 0;
              previousCountryBalances[country][tx.currency] = 0;
          }
          
          countryBalances[country][tx.currency] += tx.amount;
          
          // For demonstration, we'll calculate a "previous" balance
          if (tx.status === 'Completed') {
              const changePercent = 0.1 + Math.random() * 0.1; // 10-20% variation
              previousCountryBalances[country][tx.currency] += tx.amount * (1 - changePercent);
          }
      });
      
      return { current: countryBalances, previous: previousCountryBalances };
  }, [filteredTransactions]);
  
  // Calculate total balance
  const totalBalance = React.useMemo(() => {
      let total = 0;
      let previousTotal = 0;
      
      // For simplicity, we'll convert everything to USD at fixed rates
      const exchangeRates: { [currency: string]: number } = {
          'AED': 0.27, 'SAR': 0.27, 'QAR': 0.27, 'KWD': 3.27, 'BHD': 2.65, 'USD': 1
      };
      
      Object.values(balanceByCountry.current).forEach(currencies => {
          Object.entries(currencies).forEach(([currency, amount]) => {
              total += amount * (exchangeRates[currency] || 1);
          });
      });
      
      Object.values(balanceByCountry.previous).forEach(currencies => {
          Object.entries(currencies).forEach(([currency, amount]) => {
              previousTotal += amount * (exchangeRates[currency] || 1);
          });
      });
      
      return { current: total, previous: previousTotal, change: total - previousTotal };
  }, [balanceByCountry]);

  // Helper functions for date handling
   const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setCustomStartDate(start);
        setCustomEndDate(end);
    };

   const handleDateRangeChange = (value: string) => {
        setSelectedDateRange(value);
        if (value === 'Custom date') {
            setIsDatePickerVisible(true);
        } else {
            setIsDatePickerVisible(false);
            setCustomStartDate(null);
            setCustomEndDate(null);
        }
    };

    // Handler for copying ID
    const handleCopyID = async (id: string) => {
      try {
        await navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
      } catch (err) {
        console.error('Failed to copy ID: ', err);
      }
    };

    // Handler for row click to open details panel
    const handleRowClick = (transaction: Transaction) => {
      setSelectedTransaction(transaction);
      setTimeout(() => setIsPanelVisible(true), 10);
    };

    // Handler to close the details panel
    const handleCloseDetailsPanel = () => {
      setIsPanelVisible(false);
      setTimeout(() => setSelectedTransaction(null), 200);
    };

  // Calculate average fee percentage
  const avgFeePercentage = React.useMemo(() => {
    const paymentTransactions = filteredTransactions.filter(tx => 
      tx.type.includes('Card Payment') && tx.feeAmount !== undefined && tx.amount > 0
    );
    
    if (paymentTransactions.length === 0) return 0;
    
    const totalAmount = paymentTransactions.reduce((sum, tx) => sum + tx.amount, 0);
    const totalFees = paymentTransactions.reduce((sum, tx) => sum + (tx.feeAmount || 0), 0);
    
    return (totalFees / totalAmount) * 100;
  }, [filteredTransactions]);

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        {/* Back Button */}
        <button
            onClick={() => window.history.back()}
            style={{
                background: 'none',
                border: 'none',
                padding: 0,
                marginBottom: '18px',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                transition: 'color 0.2s ease'
            }}
        >
            <ArrowLeft size={16} style={{ marginRight: '6px' }} />
            Back to Wallet Dashboard
        </button>

        {/* Header Area */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: '1.2' }}>Acquiring Wallet</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>View balances and transactions for the Acquiring payment product.</p>
          </div>
        </div>

        {/* Filters Row */}
        <div style={{ padding: '16px 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
            {/* Filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <DropdownFilter label="Date" options={dateRangeOptions} selectedValue={selectedDateRange} onChange={handleDateRangeChange} />
                {isDatePickerVisible && (
                    <div style={{ marginLeft: '-6px' }}>
                        <RangeDatePicker startDate={customStartDate} endDate={customEndDate} onChange={handleCustomDateChange} />
                    </div>
                )}
                <DropdownFilter label="Type" options={typeOptions} selectedValue={selectedType} onChange={setSelectedType} />
                <DropdownFilter label="Country" options={countryOptions} selectedValue={selectedCountry} onChange={setSelectedCountry} />
                <DropdownFilter label="Status" options={statusOptions} selectedValue={selectedStatus} onChange={setSelectedStatus} />
                <DropdownFilter label="Card" options={cardTypeOptions} selectedValue={selectedCardType} onChange={setSelectedCardType} />
            </div>
            {/* Search */}
            <div style={{ marginLeft: 'auto' }}>
                <input
                  type="search"
                  placeholder="Search transactions..."
                  style={{
                      padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px',
                      minWidth: '200px'
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
        </div>

        {/* Dashboard Grid */}
        <div style={{ marginBottom: '32px' }}>
          {/* Metrics Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
            marginBottom: '64px'
          }}>
            <MainBalanceWidget 
              title="Available Balance"
              totalBalance={totalBalance.current} 
              change={totalBalance.change} 
              currency="USD" 
              icon={<CreditCard />}
            />

            <MetricWidget 
              title="Processing Volume"
              value={formatCurrency(acquiringMetrics.totalProcessVolume, 'USD')} 
              icon={<Activity />} 
              secondaryValue="+7.3% from last month"
              bgColor="#f0f9ff"
              iconColor="#0284c7"
            />
            
            <MetricWidget 
              title="Fee Revenue" 
              value={formatCurrency(acquiringMetrics.totalFees, 'USD')} 
              icon={<Percent />} 
              secondaryValue={`${avgFeePercentage.toFixed(2)}% avg fee rate`} 
              bgColor="#f0fdf4"
              iconColor="#10b981"
            />

            <MetricWidget 
              title="Refund Volume" 
              value={formatCurrency(acquiringMetrics.refundVolume, 'USD')} 
              icon={<RefreshCw />} 
              secondaryValue={`${(acquiringMetrics.refundVolume / acquiringMetrics.totalProcessVolume * 100).toFixed(1)}% of sales`} 
              bgColor="#ffedd5" 
              iconColor="#f97316" 
            />
            
            <MetricWidget 
              title="Active Merchants" 
              value={acquiringMetrics.activeMerchants.toString()} 
              icon={<Briefcase />}
              bgColor="#eef2ff" 
              iconColor="#4f46e5" 
            />
          </div>
        </div>

        {/* Transaction History Section */}
        <section style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
          {/* Table */}
          {filteredTransactions.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>ID</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Created at</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Partner</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Country</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Category</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Type</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Card</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'right' }}>Amount</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx, index) => (
                  <tr
                    key={tx.id}
                    data-transaction-row="true"
                    style={{
                      borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                      backgroundColor: hoveredRowIndex === index ? '#f9fafb' : 'transparent',
                      transition: 'background-color 0.1s ease-in-out', height: '35px'
                    }}
                    onMouseEnter={() => setHoveredRowIndex(index)}
                    onMouseLeave={() => setHoveredRowIndex(null)}
                    onClick={() => handleRowClick(tx)}
                  >
                    {/* ID Cell */}
                    <td style={{ padding: '7px 16px', width: '60px', textAlign: 'center' }}>
                       <div
                         style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', height: '100%', position: 'relative' }}
                         onClick={(e) => { e.stopPropagation(); handleCopyID(tx.id); }}
                         title={copiedId === tx.id ? 'ID copied' : `Click to copy ID: ${tx.id}`}
                         onMouseEnter={() => setHoveredIdIcon(tx.id)}
                         onMouseLeave={() => setHoveredIdIcon(null)}
                       >
                         {copiedId === tx.id ? <CheckIcon style={{ color: '#10b981' }} size={16} />
                           : hoveredIdIcon === tx.id ? <CopyIcon style={{ color: '#6b7280' }} size={14} />
                           : <HashIcon style={{ color: '#9ca3af' }} size={14} />}
                       </div>
                    </td>
                    {/* Created at Cell */}
                    <td style={{ padding: '7px 16px', color: '#374151', whiteSpace: 'nowrap', width: '140px' }}>
                        {`${formatDateTime(tx.transactionDate).date}, ${formatDateTime(tx.transactionDate).time}`}
                    </td>
                    {/* Partner Cell */}
                    <td style={{ padding: '7px 16px' }}>
                      <div style={{ fontWeight: 400, color: '#111827' }}>{tx.partner}</div>
                    </td>
                    {/* Country Cell */}
                    <td style={{ padding: '7px 16px', color: '#374151', width: '80px' }}>{tx.location || 'N/A'}</td>
                    {/* Category Cell */}
                    <td style={{ padding: '7px 16px' }}>
                        <CategoryBadge category={getTransactionCategoryBase(tx)} />
                    </td>
                    {/* Type Cell */}
                    <td style={{ padding: '7px 16px', fontWeight: 400, color: '#111827' }}>{tx.type}</td>
                    {/* Card Type Cell (New for Acquiring) */}
                    <td style={{ padding: '7px 16px', color: '#374151' }}>{tx.cardType || '-'}</td>
                    {/* Amount Cell */}
                    <td style={{ padding: '7px 16px', fontWeight: 500, color: '#111827', textAlign: 'right' }}>
                      {formatCurrency(tx.amount, tx.currency)}
                    </td>
                    {/* Status Cell */}
                    <td style={{ padding: '7px 16px', textAlign: 'center' }}>
                      <StatusBadge status={tx.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
             // Empty State
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', borderTop: '1px solid #e5e7eb', margin: '0 24px' }}>
               <FileText size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', fontWeight: 500, color: '#111827', marginBottom: '4px' }}>No Acquiring Transactions Found</p>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Try adjusting your filter criteria.</p>
            </div>
           )}
        </section>
      </div>

      {/* Render Transaction Details Panel */}
      {selectedTransaction && (
        <TransactionDetailsPanel
          transaction={selectedTransaction}
          onClose={handleCloseDetailsPanel}
          isVisible={isPanelVisible}
        />
      )}
    </MainLayout>
  );
};

export default AcquiringWalletPage;
